import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import KeywordAssist from './components/KeywordAssist';
import { KeywordAssistProvider } from './Providers/KAProviders';
import { useQuery } from '@tanstack/react-query';
import getKeywordsAssistance4Url from './api/getKA4Url';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { StripeLoader } from 'src/common/StripeLoader';
import { useGetAgentsStatus } from '../hooks/useGetAgentsStatus';
import { useOrbitAISSE } from '../hooks/useOrbitAISSE';
import { Breadcrumbs, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import KeywordAssistHeader from './components/KeywordAssistHeader';
import { keywordResearchSubmenuTitles, paths } from 'src/common/config/TextMessage';
import { useNavigate } from 'react-router';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';
import { Card } from 'src/components/UI/Card';

function KeywordAssistFallback() {
  const navigate = useNavigate();

  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              sx={keywordResearchDashBoardCss?.navigateNextIcon}
            />
          }
          aria-label="breadcrumb"
        >
          <Typography
            sx={{
              ...keywordResearchDashBoardCss?.breadCrumbLightText,
              cursor: 'pointer'
            }}
            onClick={() => navigate(paths.keywordResearch)}
          >
            Keyword Research
          </Typography>
          <Typography
            sx={keywordResearchDashBoardCss?.breadCrumbText}
          >
            {keywordResearchSubmenuTitles.keywordAssist}
          </Typography>
        </Breadcrumbs>
        <KeywordAssistHeader />
      </div>
      <Card>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Keyword Assist</p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </Card>
    </div>
  );
}

function KeywordAssistContent() {

    // get site id from redux
    const { _id, user_id } = useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails);

    useOrbitAISSE(_id);

    const { data, isLoading, isPending, refetch: refetchKeyword } = useQuery(getKeywordsAssistance4Url(user_id, _id));

    const { data: agentsStatus } = useGetAgentsStatus(_id);

    const isKeywordIntelligenceProcessing = agentsStatus?.agents?.['keyword-intelligence']?.status === 'processing';

    useEffect(() => {
        if (!isKeywordIntelligenceProcessing && agentsStatus) {
            refetchKeyword();
        }
    }, [isKeywordIntelligenceProcessing, agentsStatus, refetchKeyword]);

    if (isLoading || isPending) {
        return (
            <div
                className='-mt-8 pt-8'
                style={{
                    minHeight: 'calc(100vh - 51px)',
                    backgroundColor: 'var(--color-bg-cream)'
                }}
            >
                <StripeLoader />
            </div>
        )
    }

    return (
        <KeywordAssistProvider data={data}>
            <KeywordAssist isProcessing={isKeywordIntelligenceProcessing} />
        </KeywordAssistProvider>
    );
}

function KeywordAssistPage() {
    return (
        <>
            <Helmet>
                <title>SEOSpace | Keyword Assist</title>
            </Helmet>

            <OrbitAIGuard fallback={<KeywordAssistFallback />}>
                <OrbitAgentSiteGuard
                    title={keywordResearchSubmenuTitles.keywordAssist}
                    headerComponent={<KeywordAssistHeader />}
                >
                    <KeywordAssistContent />
                </OrbitAgentSiteGuard>
            </OrbitAIGuard>
        </>
    );
}

export default KeywordAssistPage;

